import { useState } from "react";
import {
    Heart,
    Mail,
    Phone,
    MapPin,
    Send,
    Clock,
    Truck,
    Sparkles,
    ChevronDown,
    Instagram,
    Facebook,
} from "lucide-react";
import { toast } from "sonner";

const TikTok = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.14V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21z" />
    </svg>
);

const FAQS = [
    {
        q: "How does porch pickup work?",
        a:
            "Local Pass Christian customers can choose 'Porch Pickup' at checkout. Once your order is ready, we'll message you the pickup window and have it set out, beautifully packaged, on the porch for you.",
    },
    {
        q: "Do you ship outside of Florida?",
        a:
            "Yes! We ship anywhere in the continental U.S. Orders typically ship within 2–4 business days, and you'll get a tracking link as soon as it goes out.",
    },
    {
        q: "What's your return policy?",
        a:
            "We accept returns on most items within 14 days of delivery, provided they are unused and in original condition. A few items (final sale, jewelry, and personalized pieces) are non-returnable.",
    },
    {
        q: "Can I customize or personalize an item?",
        a:
            "Several of our drinkware and home decor items can be personalized. Message us through the contact form and we'll work with you on the details.",
    },
    {
        q: "How can I stay in the loop on new arrivals?",
        a:
            "Follow us on Instagram, Facebook, and TikTok — we drop sneak peeks, launches, and exclusive offers there first.",
    },
];

function FAQItem({ q, a, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            className="border-b border-bb-warm-brown/20 fade-up"
            data-testid={`faq-item-${idx}`}
            style={{ transitionDelay: `${idx * 60}ms` }}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full flex items-center justify-between text-left py-4 group"
                data-testid={`faq-toggle-${idx}`}
            >
                <span className="font-serif-display text-bb-text text-base md:text-lg pr-4 group-hover:text-bb-pink-script transition-colors">
                    {q}
                </span>
                <ChevronDown
                    size={20}
                    className={`text-bb-rose-gold shrink-0 transition-transform duration-500 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            <div
                className={`grid transition-all duration-500 ease-out ${
                    open ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"
                }`}
            >
                <div className="overflow-hidden">
                    <p className="text-sm md:text-base text-bb-text-soft leading-relaxed">{a}</p>
                </div>
            </div>
        </div>
    );
}

export default function Contact() {
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const encode = (data) =>
        Object.keys(data)
            .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
            .join("&");

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        if (data["bot-field"]) return;
        setSubmitting(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({ "form-name": "contact", ...data }),
            });
            // Netlify Forms returns 200 with HTML on success. In the preview env (no Netlify),
            // the dev server returns 404 — still gracefully fall back to mailto.
            if (res.ok) {
                setDone(true);
                toast("Message sent!", {
                    description: "Thank you – we'll be in touch soon.",
                });
                form.reset();
            } else {
                // Fallback for non-Netlify environments — open the user's mail client
                const subject = encodeURIComponent(`Beyond Blessed inquiry: ${data.subject || "General"}`);
                const body = encodeURIComponent(
                    `Name: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone || ""}\n\n${data.message}`
                );
                window.location.href = `mailto:Beyond.03.Blessed@gmail.com?subject=${subject}&body=${body}`;
                toast("Opening your email app", {
                    description: "Forms only deliver once deployed on Netlify.",
                });
            }
        } catch (err) {
            toast("Could not send your message", {
                description: "Please try again or email us directly.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="cream-textured">
            {/* ===== Header ===== */}
            <section className="relative py-16 md:py-24 text-center sparkle-overlay">
                <div className="absolute inset-0 leopard-edge opacity-30 pointer-events-none" />
                <div className="relative z-10 max-w-3xl mx-auto px-6">
                    <p className="font-script text-3xl text-bb-pink-script">Say Hello</p>
                    <h1 className="font-serif-display text-4xl md:text-6xl tracking-[0.1em] uppercase text-bb-text">
                        Contact Us
                    </h1>
                    <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                        <span className="swoosh w-14" />
                        <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                        <span className="swoosh w-14" />
                    </div>
                    <p className="mt-5 text-bb-text-soft leading-relaxed">
                        We'd love to hear from you. Whether you have a product question, custom request, or just want to say hi – we read every message.
                    </p>
                </div>
            </section>

            {/* ===== Quick info cards ===== */}
            <section className="pb-4">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {[
                        {
                            icon: Mail,
                            label: "Email",
                            value: "Beyond.03.Blessed@gmail.com",
                            href: "mailto:Beyond.03.Blessed@gmail.com",
                            sub: "We reply within 24 hours",
                            testid: "info-email",
                        },
                        {
                            icon: Phone,
                            label: "Phone",
                            value: "(228) 216-7117",
                            href: "tel:+12282167117",
                            sub: "Mon – Sat, 9am – 5pm",
                            testid: "info-phone",
                        },
                        {
                            icon: MapPin,
                            label: "Pickup Location",
                            value: "10074 Lacy Niolet Road, Pass Christian, MS 39571",
                            href: "https://maps.google.com/?q=10074+Lacy+Niolet+Road,+Pass+Christian,+MS+39571",
                            sub: "Porch pickup available locally",
                            testid: "info-location",
                        },
                    ].map((c, i) => (
                        <div
                            key={c.label}
                            data-testid={`contact-info-card-${c.testid}`}
                            className="bg-white border border-bb-warm-brown/25 rounded-md p-6 text-center fade-up hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <div className="w-12 h-12 mx-auto rounded-full bg-bb-blush-2 flex items-center justify-center">
                                <c.icon size={20} className="text-bb-rose-gold" />
                            </div>
                            <p className="mt-3 text-xs tracking-[0.25em] uppercase text-bb-pink-script">
                                {c.label}
                            </p>
                            {c.href ? (
                                <a
                                    href={c.href}
                                    className="block mt-1 font-serif-display text-bb-text text-sm md:text-base break-all hover:text-bb-pink-script transition-colors"
                                >
                                    {c.value}
                                </a>
                            ) : (
                                <p className="mt-1 font-serif-display text-bb-text text-sm md:text-base">
                                    {c.value}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-bb-text-soft">{c.sub}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ===== Form + sidebar ===== */}
            <section className="py-12 md:py-16">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
                    {/* Form */}
                    <div className="lg:col-span-3 fade-up">
                        <div className="bg-white border border-bb-warm-brown/25 rounded-md p-6 md:p-10 shadow-lg shadow-bb-warm-brown/10">
                            <div className="flex items-center gap-3">
                                <Sparkles size={18} className="text-bb-pink-script animate-spin-slow" />
                                <h2 className="font-serif-display tracking-[0.18em] uppercase text-xl md:text-2xl text-bb-text">
                                    Send Us a Message
                                </h2>
                            </div>
                            <div className="mt-2 swoosh w-20" />
                            <p className="mt-3 text-sm text-bb-text-soft">
                                Tell us a little about what you're looking for. We typically reply within one business day.
                            </p>

                            {done ? (
                                <div
                                    className="mt-8 text-center py-12 px-4"
                                    data-testid="contact-success"
                                >
                                    <Heart
                                        size={32}
                                        className="mx-auto fill-current text-bb-pink-script animate-heart-beat"
                                    />
                                    <p className="mt-4 font-script text-3xl text-bb-pink-script">
                                        Thank you!
                                    </p>
                                    <p className="mt-2 text-bb-text-soft max-w-md mx-auto">
                                        Your message has been sent. We'll get back to you as soon as we can – check your inbox for our reply.
                                    </p>
                                </div>
                            ) : (
                                <form
                                    name="contact"
                                    method="POST"
                                    data-netlify="true"
                                    data-netlify-honeypot="bot-field"
                                    onSubmit={handleSubmit}
                                    className="mt-6 space-y-5"
                                    data-testid="contact-form"
                                >
                                    <input type="hidden" name="form-name" value="contact" />
                                    <p hidden>
                                        <label>
                                            Don't fill this out: <input name="bot-field" />
                                        </label>
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <label className="block">
                                            <span className="block text-xs tracking-[0.18em] uppercase text-bb-text-soft mb-2">
                                                Name *
                                            </span>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                data-testid="contact-name"
                                                className="w-full px-4 py-3 rounded-md border border-bb-warm-brown/30 bg-bb-cream/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bb-pink-script transition"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="block text-xs tracking-[0.18em] uppercase text-bb-text-soft mb-2">
                                                Email *
                                            </span>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                data-testid="contact-email"
                                                className="w-full px-4 py-3 rounded-md border border-bb-warm-brown/30 bg-bb-cream/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bb-pink-script transition"
                                            />
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <label className="block">
                                            <span className="block text-xs tracking-[0.18em] uppercase text-bb-text-soft mb-2">
                                                Phone
                                            </span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                data-testid="contact-phone"
                                                className="w-full px-4 py-3 rounded-md border border-bb-warm-brown/30 bg-bb-cream/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bb-pink-script transition"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="block text-xs tracking-[0.18em] uppercase text-bb-text-soft mb-2">
                                                I'm reaching out about
                                            </span>
                                            <select
                                                name="subject"
                                                data-testid="contact-subject"
                                                defaultValue="General question"
                                                className="w-full px-4 py-3 rounded-md border border-bb-warm-brown/30 bg-bb-cream/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bb-pink-script transition appearance-none"
                                            >
                                                <option>General question</option>
                                                <option>Order or shipping</option>
                                                <option>Porch pickup</option>
                                                <option>Custom / personalized item</option>
                                                <option>Wholesale / collaboration</option>
                                            </select>
                                        </label>
                                    </div>
                                    <label className="block">
                                        <span className="block text-xs tracking-[0.18em] uppercase text-bb-text-soft mb-2">
                                            Message *
                                        </span>
                                        <textarea
                                            name="message"
                                            required
                                            rows={5}
                                            data-testid="contact-message"
                                            className="w-full px-4 py-3 rounded-md border border-bb-warm-brown/30 bg-bb-cream/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-bb-pink-script transition resize-y"
                                        />
                                    </label>
                                    <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
                                        <p className="text-xs text-bb-text-soft">
                                            By submitting you agree to be contacted by Beyond Blessed Gifts & More LLC.
                                        </p>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="btn-black btn-shine disabled:opacity-60 disabled:cursor-not-allowed"
                                            data-testid="contact-submit"
                                        >
                                            {submitting ? "Sending..." : "Send Message"}
                                            <Send size={14} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:col-span-2 space-y-6 fade-up">
                        {/* Hours */}
                        <div className="bg-bb-black text-white rounded-md p-6 md:p-7">
                            <div className="flex items-center gap-3">
                                <Clock size={18} className="text-bb-pink-script" />
                                <h2 className="font-serif-display tracking-[0.18em] uppercase text-bb-blush text-base">
                                    Boutique Hours
                                </h2>
                            </div>
                            <div className="mt-2 swoosh w-16" />
                            <ul className="mt-5 text-sm space-y-2 text-white/85">
                                {[
                                    ["Monday – Friday", "9:00 AM – 5:00 PM"],
                                    ["Saturday", "9:00 AM – 5:00 PM"],
                                    ["Sunday", "Closed"],
                                ].map(([day, time]) => (
                                    <li
                                        key={day}
                                        className="flex justify-between border-b border-white/10 pb-2 last:border-b-0 last:pb-0"
                                    >
                                        <span>{day}</span>
                                        <span className="text-bb-blush">{time}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-5 text-xs text-white/60">
                                Online orders are processed daily and shipped within 2–4 business days.
                            </p>
                        </div>

                        {/* Porch pickup */}
                        <div className="bg-bb-blush-2 rounded-md p-6 md:p-7 border border-bb-warm-brown/25">
                            <div className="flex items-center gap-3">
                                <Truck size={18} className="text-bb-rose-gold" />
                                <h3 className="font-serif-display tracking-[0.18em] uppercase text-bb-text text-base">
                                    Porch Pickup
                                </h3>
                            </div>
                            <div className="mt-2 swoosh w-16" />
                            <p className="mt-4 text-sm text-bb-text-soft leading-relaxed">
                                Local to Pass Christian? Choose porch pickup at checkout for a fast, easy and convenient experience. We'll message you when your order is ready.
                            </p>
                        </div>

                        {/* Social */}
                        <div className="bg-white border border-bb-warm-brown/25 rounded-md p-6 md:p-7">
                            <h3 className="font-serif-display tracking-[0.18em] uppercase text-bb-text text-base">
                                Follow Along
                            </h3>
                            <div className="mt-2 swoosh w-16" />
                            <p className="mt-4 text-sm text-bb-text-soft">
                                For sneak peeks, launches and giveaways – follow us on social.
                            </p>
                            <div className="mt-4 flex items-center gap-3">
                                {[
                                    { Icon: Instagram, label: "Instagram" },
                                    { Icon: Facebook, label: "Facebook" },
                                    { Icon: TikTok, label: "TikTok" },
                                ].map(({ Icon, label }) => (
                                    <a
                                        key={label}
                                        href="#"
                                        aria-label={label}
                                        data-testid={`contact-social-${label.toLowerCase()}`}
                                        className="w-10 h-10 rounded-full bg-bb-pink-script/90 hover:bg-bb-black text-white flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 icon-hover"
                                    >
                                        <Icon size={16} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* ===== FAQ ===== */}
            <section className="bg-white/60 border-y border-bb-warm-brown/15 py-16 md:py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <div className="text-center fade-up">
                        <p className="font-script text-3xl text-bb-pink-script">Got Questions?</p>
                        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[0.12em] uppercase text-bb-text mt-2">
                            Frequently Asked
                        </h2>
                        <div className="mt-3 swoosh w-24 mx-auto" />
                    </div>
                    <div className="mt-10" data-testid="faq-list">
                        {FAQS.map((f, i) => (
                            <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
                        ))}
                    </div>
                    <p className="mt-10 text-center text-sm text-bb-text-soft">
                        Still have a question?{" "}
                        <a
                            href="mailto:Beyond.03.Blessed@gmail.com"
                            className="text-bb-pink-script underline-offset-4 hover:underline"
                        >
                            Email us
                        </a>{" "}
                        and we'll get right back to you.
                    </p>
                </div>
            </section>
        </div>
    );
}
